import {useDispatch, useSelector} from "react-redux";
import {globalDataSelector} from "../../../../../../redux/slices/globalDataSlice";
import {managementSelector} from "../../../../../../redux/slices/managementSlice";
import useCustomSnackbar from "../../../../../../hooks/useCustomSnackbar";
import {useSnackbar} from "notistack";
import {Link, useSearchParams} from "react-router-dom";
import useGetSearchParams from "../../../../../../hooks/useGetSearchParams";
import useSetSearchParams from "../../../../../../hooks/useSetSearchParams";
import {useTranslation} from "react-i18next";
import {useDebounce} from "../../../../../../hooks/useDebounce";
import {page, search} from "../../../../Statistics/IOS/IOSClicksStats/IOSClicksStats";
import React, {useCallback, useEffect} from "react";
import {params, teamsTabOrderingList, teamsUsersOrderingList} from "../../../../../../utils/vars/params";
import s from './teamsTab.module.scss'
import ParamsSort from "../../../../../../components/ParamsSort/ParamsSort";
import GradientBorder from "../../../../../../components/GradientBorder/GradientBorder";
import EmptyList from "../../../../../../components/EmptyList/EmptyList";
import CustomSkeleton from "../../../../../../components/UiKit/CustomSkeleton/CustomSkeleton";
import {convertDate} from "../../../../../../utils/helpers/formatTime";
import MuiTooltip from "../../../../../../components/UiKit/MuiTooltip/MuiTooltip";
import SvgSelector from "../../../../../../components/SvgSelector/SvgSelector";
import Pagination from "../../../../../../components/Pagination/Pagination";
import {
    editTeamName,
    fetchAllTeams,
    fetchTeamsWithParams, removeTeam,
    switchUserStatus,
    teamsSelector
} from "../../../../../../redux/slices/teamsSlice";
import {getTableDefaultParams} from "../../../../../../api/helpers/scripts";
import {
    INVALID_PAGE_ERROR,
    snackbar_error,
    snackbar_info,
    snackbar_success
} from "../../../../../../utils/vars/staticVariables";
import AddTeamPopup from "../../Popups/AddTeamPopup/AddTeamPopup";
import {divide} from "lodash/math";
import TeamTr from "./TeamTr/TeamTr";

const TeamsTab = ({isAddTeamPopupActive, checkIfCanAddUserToTeam,  setAddTeamPopupActive, setCurrentTab, setOwnerAddUserPopupActive, fetchTeams}) => {
    const {isCollapsedSidebar} = useSelector(globalDataSelector);

    const {teamsWithParamsPending, teamsWithParamsList} = useSelector(teamsSelector);

    const dispatch = useDispatch();

    const showSnackbar = useCustomSnackbar();
    const {closeSnackbar} = useSnackbar();

    const [searchParams, setSearchParams] = useSearchParams();

    const {search: searchValue, page: pageValue} = useGetSearchParams();

    const updateSearchParams = useSetSearchParams(setSearchParams);

    const {t} = useTranslation();

    const [name, setName] = useDebounce((value) => {
        if (value) {
            updateSearchParams(search, value);
        } else {
            updateSearchParams(search, value, true);
        }
    }, 600);

    useEffect(() => {
        if (!searchValue) {
            setName("");
        } else {
            setName(searchValue);
        }
    }, [searchParams]);



    useEffect(() => {
        const promise = fetchTeams();

        return () => {
            promise.abort();
        };
    }, [dispatch, searchParams]);

    const editTeamNameHandler = useCallback(async (team_id, team_name) => {
        const editTeamNameResult = await dispatch(editTeamName({team_id, team_name})).unwrap();

        if (editTeamNameResult.status === 200) {
            // setActiveSetNotePopup(false);
            fetchTeams();
            showSnackbar(t("notif_renamed_flow"), snackbar_success);
        } else {
            showSnackbar(t("notif_failed_flow_renaming"), snackbar_error);
        }
    }, [dispatch, fetchTeams, showSnackbar, t]);


    const onRemoveTeamHandler = useCallback(
        async (team_id) => {
            const pendingDeleteSnackbar = showSnackbar(
                t("notif_delete_flow"),
                snackbar_info,
                true,
            );

            const removeTeamResult = await dispatch(removeTeam(team_id)).unwrap();

            const {status} = removeTeamResult

            if (status === 200) {
                fetchTeams();
                closeSnackbar(pendingDeleteSnackbar);
                showSnackbar(t("team_removed"), snackbar_success);
            } else {
                closeSnackbar(pendingDeleteSnackbar);
                showSnackbar(t("failed_remove_team"), snackbar_error);
            }
        },
        [closeSnackbar, dispatch, fetchTeams, showSnackbar],
    );


    const trBlock = <tr>
        {teamsTabOrderingList.map(({ordering, name}, index) => (
            <th key={index}>
                <div className={s.headerContent}>
                    {t(name)}
                    {
                        ordering && <ParamsSort
                            sortBy={ordering}
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                        />
                    }
                </div>
            </th>
        ))}
    </tr>

    return (
        <div className={s.usersTableWrapper}>
            <GradientBorder className={s.gradientBorderCustom}/>
            <AddTeamPopup
                active={isAddTeamPopupActive}
                setActive={setAddTeamPopupActive}
                setCurrentTab={setCurrentTab}
                request={fetchTeams}
            />
            <div className={s.pageInnerContent}>
                <div className={s.innerContentContainer}>
                    {teamsWithParamsList.length === 0 && !teamsWithParamsPending ? (
                        <EmptyList/>
                    ) : teamsWithParamsPending ? (
                        <section className={s.body}>
                            <table>
                                {trBlock}
                            </table>
                            <div className={s.skeletonWrapper}>
                                <CustomSkeleton styles={s.skeletonTable}/>
                            </div>
                        </section>
                    ) : (
                        <section className={s.body}>
                            <table>
                                <thead>
                                {trBlock}
                                </thead>
                                <tbody>
                                {teamsWithParamsList?.map((team) => <TeamTr
                                    {...team}
                                    key={team.id}
                                    editTeamNameHandler={editTeamNameHandler}
                                    setOwnerAddUserPopupActive={setOwnerAddUserPopupActive}
                                    onRemoveTeamHandler={onRemoveTeamHandler}
                                    checkIfCanAddUserToTeam={checkIfCanAddUserToTeam}
                                />)}
                                </tbody>
                            </table>
                        </section>
                    )}
                </div>
            </div>

            <div
                className={`${s.paginationContainer} ${isCollapsedSidebar === "true" ? s.collapsed : ""}`}
            >
                <div className={s.newRecordsCount}>
                    {t("total")}:{" "}
                    {!teamsWithParamsPending && <span>{teamsWithParamsList.length}</span>}
                </div>
            </div>
        </div>
    );
};

export default TeamsTab;